import React, { useState, useEffect } from 'react';
import { X, Sparkles, Save, Loader2, Plus, Trash2, Zap, Database } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Textarea } from '../../ui/Textarea';
import { Dropdown } from '../../ui/Dropdown';
import { 
  GitHubIcon, 
  JiraIcon, 
  ConfluenceIcon, 
  SlackIcon, 
  AzureDevOpsIcon,
  TeamsIcon
} from '../../common/BrandIcons';
import api, { AIModel, Datasource, IntegrationSummary } from '../../../services/api';
import type { Assistant } from '../../../types';
import styles from './EditAssistantModal.module.css';

export interface EditAssistantModalProps {
  isOpen: boolean;
  assistant: Assistant;
  onClose: () => void;
  onSave: (updatedAssistant: Partial<Assistant>) => Promise<void>;
}

const categoryOptions = [
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'devops', label: 'DevOps' },
  { value: 'data', label: 'Data' },
  { value: 'analytics', label: 'Analytics' },
];

const fallbackModelOptions = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
];

export const EditAssistantModal: React.FC<EditAssistantModalProps> = ({
  isOpen,
  assistant,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: assistant.name,
    description: assistant.description,
    systemInstructions: assistant.systemInstructions || '',
    llmModel: assistant.llmModel || 'gpt-4o',
    categories: assistant.categories || [],
    conversationStarters: assistant.conversationStarters || [],
    datasourceIds: assistant.datasourceIds || [],
    integrationIds: assistant.integrationIds || [],
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'behavior' | 'integrations'>('general');
  
  // State for LLM models
  const [llmModels, setLlmModels] = useState<AIModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);
  
  // State for datasources
  const [availableDatasources, setAvailableDatasources] = useState<Datasource[]>([]);
  const [loadingDatasources, setLoadingDatasources] = useState(true);
  
  // State for integrations
  const [connectedIntegrations, setConnectedIntegrations] = useState<IntegrationSummary[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  // Reset form when assistant changes
  useEffect(() => {
    setFormData({
      name: assistant.name,
      description: assistant.description,
      systemInstructions: assistant.systemInstructions || '',
      llmModel: assistant.llmModel || 'gpt-4o',
      categories: assistant.categories || [],
      conversationStarters: assistant.conversationStarters || [],
      datasourceIds: assistant.datasourceIds || [],
      integrationIds: assistant.integrationIds || [],
    });
    setError(null);
  }, [assistant, isOpen]);

  // Fetch LLM models
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const response = await api.getAIModels();
        if (response.success && response.data?.models) {
          setLlmModels(response.data.models);
        }
      } catch (err) {
        console.error('Failed to fetch LLM models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, [isOpen]);

  // Fetch datasources
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchDatasources = async () => {
      try {
        setLoadingDatasources(true);
        const response = await api.listAllDatasources();
        if (response.success && response.data) {
          setAvailableDatasources(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error('Failed to fetch datasources:', err);
      } finally {
        setLoadingDatasources(false);
      }
    };
    fetchDatasources();
  }, [isOpen]);

  // Fetch integrations
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchIntegrations = async () => {
      try {
        setLoadingIntegrations(true);
        const response = await api.getConnectedIntegrations();
        if (response.success && response.data) {
          const integrations = Array.isArray(response.data) ? response.data : [];
          setConnectedIntegrations(integrations.filter(i => i.connected));
        }
      } catch (err) {
        console.error('Failed to fetch integrations:', err);
      } finally {
        setLoadingIntegrations(false);
      }
    };
    fetchIntegrations();
  }, [isOpen]);

  const llmModelOptions = llmModels.length > 0
    ? llmModels.map(model => ({
        value: model.id,
        label: `${model.name} (${model.publisher})`,
      }))
    : fallbackModelOptions;

  const handleFieldChange = <K extends keyof typeof formData>(
    field: K,
    value: typeof formData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleAddConversationStarter = () => {
    setFormData(prev => ({
      ...prev,
      conversationStarters: [...prev.conversationStarters, ''],
    }));
  };

  const handleUpdateConversationStarter = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      conversationStarters: prev.conversationStarters.map((s, i) => i === index ? value : s),
    }));
  };

  const handleRemoveConversationStarter = (index: number) => {
    setFormData(prev => ({
      ...prev,
      conversationStarters: prev.conversationStarters.filter((_, i) => i !== index),
    }));
  };

  const handleDatasourceToggle = (datasourceId: string) => {
    setFormData(prev => ({
      ...prev,
      datasourceIds: prev.datasourceIds.includes(datasourceId)
        ? prev.datasourceIds.filter(id => id !== datasourceId)
        : [...prev.datasourceIds, datasourceId],
    }));
  };

  const handleIntegrationToggle = (provider: string) => {
    setFormData(prev => ({
      ...prev,
      integrationIds: prev.integrationIds.includes(provider)
        ? prev.integrationIds.filter(p => p !== provider)
        : [...prev.integrationIds, provider],
    }));
  };

  const handleGenerateWithAI = async () => {
    if (!formData.systemInstructions.trim()) {
      setError('Please enter some initial instructions to enhance');
      return;
    }

    setIsEnhancing(true);
    setError(null);

    try {
      const response = await api.enhanceWithAI(formData.systemInstructions, {
        type: 'system_instructions',
        assistantName: formData.name,
        assistantDescription: formData.description,
      });

      if (response.success && response.data?.enhancedText) {
        setFormData(prev => ({
          ...prev,
          systemInstructions: response.data.enhancedText,
        }));
      } else {
        setError('Failed to enhance text. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate enhanced instructions');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        name: formData.name.trim(),
        description: formData.description.trim(),
        systemInstructions: formData.systemInstructions.trim(),
        llmModel: formData.llmModel,
        categories: formData.categories,
        conversationStarters: formData.conversationStarters.filter(s => s.trim()),
        datasourceIds: formData.datasourceIds,
        integrationIds: formData.integrationIds,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <h2>Edit Assistant</h2>
            <p>Update {assistant.name}'s configuration</p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'general' ? styles.active : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'behavior' ? styles.active : ''}`}
            onClick={() => setActiveTab('behavior')}
          >
            Behavior & Logic
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'integrations' ? styles.active : ''}`}
            onClick={() => setActiveTab('integrations')}
          >
            Integrations
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          <div className={styles.content}>
            {activeTab === 'general' && (
              <div className={styles.tabContent}>
                <div className={styles.formGroup}>
                  <Input
                    label="Name"
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="Assistant name"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <Textarea
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Describe what this assistant does..."
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <Dropdown
                    label="Categories"
                    options={categoryOptions}
                    value={formData.categories[0] || ''}
                    onChange={(value) => handleFieldChange('categories', [value])}
                    placeholder="Select a category"
                  />
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.labelRow}>
                    <label className={styles.label}>Conversation Starters</label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      leftIcon={<Plus size={14} />}
                      onClick={handleAddConversationStarter}
                    >
                      Add
                    </Button>
                  </div>
                  <div className={styles.startersList}>
                    {formData.conversationStarters.map((starter, index) => (
                      <div key={index} className={styles.starterRow}>
                        <input
                          type="text"
                          className={styles.starterInput}
                          value={starter}
                          onChange={(e) => handleUpdateConversationStarter(index, e.target.value)}
                          placeholder="Enter a conversation starter..."
                        />
                        <button
                          type="button"
                          className={styles.deleteButton}
                          onClick={() => handleRemoveConversationStarter(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.conversationStarters.length === 0 && (
                      <p className={styles.emptyText}>No conversation starters yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'behavior' && (
              <div className={styles.tabContent}>
                <div className={styles.formGroup}>
                  <div className={styles.labelRow}>
                    <label className={styles.label}>System Instructions</label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      leftIcon={isEnhancing ? <Loader2 size={14} className={styles.spinning} /> : <Sparkles size={14} />}
                      onClick={handleGenerateWithAI}
                      disabled={isEnhancing || !formData.systemInstructions.trim()}
                    >
                      {isEnhancing ? 'Enhancing...' : 'Generate with AI'}
                    </Button>
                  </div>
                  <textarea
                    className={styles.instructionsInput}
                    value={formData.systemInstructions}
                    onChange={(e) => handleFieldChange('systemInstructions', e.target.value)}
                    placeholder="Enter system instructions that define how this assistant should behave..."
                    rows={10}
                    disabled={isEnhancing}
                  />
                </div>

                <div className={styles.formGroup}>
                  <Dropdown
                    label={loadingModels ? 'LLM Model (Loading...)' : 'LLM Model'}
                    options={llmModelOptions}
                    value={formData.llmModel}
                    onChange={(value) => handleFieldChange('llmModel', value)}
                    disabled={loadingModels}
                  />
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className={styles.tabContent}>
                {/* Datasources */}
                <div className={styles.formGroup}>
                  <div className={styles.labelRow}>
                    <label className={styles.label}>
                      <Database size={16} />
                      <span>Datasources</span>
                    </label>
                  </div>
                  <p className={styles.helperText}>
                    Select datasources for your assistant's knowledge base.
                  </p>
                  {loadingDatasources ? (
                    <div className={styles.loadingState}>
                      <Loader2 size={16} className={styles.spinning} />
                      Loading datasources...
                    </div>
                  ) : availableDatasources.length === 0 ? (
                    <div className={styles.emptyState}>
                      No datasources available. <a href="/datasources">Create one</a>
                    </div>
                  ) : (
                    <div className={styles.checkboxGrid}>
                      {availableDatasources.map(ds => (
                        <label key={ds.id} className={styles.checkboxItem}>
                          <input
                            type="checkbox"
                            checked={formData.datasourceIds.includes(ds.id)}
                            onChange={() => handleDatasourceToggle(ds.id)}
                          />
                          <div className={styles.checkboxContent}>
                            <span className={styles.checkboxLabel}>{ds.name}</span>
                            <span className={styles.checkboxMeta}>
                              {ds.status === 'complete' ? '✓ Ready' : ds.status}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Integrations */}
                <div className={styles.formGroup}>
                  <div className={styles.labelRow}>
                    <label className={styles.label}>
                      <Zap size={16} />
                      <span>Connected Integrations</span>
                    </label>
                  </div>
                  <p className={styles.helperText}>
                    Enable integrations for this assistant to access external tools.
                  </p>
                  {loadingIntegrations ? (
                    <div className={styles.loadingState}>
                      <Loader2 size={16} className={styles.spinning} />
                      Loading integrations...
                    </div>
                  ) : connectedIntegrations.length === 0 ? (
                    <div className={styles.emptyState}>
                      No integrations connected. <a href="/integrations">Connect one</a>
                    </div>
                  ) : (
                    <div className={styles.checkboxGrid}>
                      {connectedIntegrations.map(integration => (
                        <label key={integration.provider} className={styles.checkboxItem}>
                          <input
                            type="checkbox"
                            checked={formData.integrationIds.includes(integration.provider)}
                            onChange={() => handleIntegrationToggle(integration.provider)}
                          />
                          <div className={styles.checkboxContent}>
                              <span className={styles.checkboxLabel} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {integration.provider === 'jira' && <JiraIcon size={16} />}
                                {integration.provider === 'confluence' && <ConfluenceIcon size={16} />}
                                {integration.provider === 'github' && <GitHubIcon size={16} />}
                                {integration.provider === 'slack' && <SlackIcon size={16} />}
                                {integration.provider === 'azure-devops' && <AzureDevOpsIcon size={16} />}
                                {integration.provider === 'teams' && <TeamsIcon size={16} />}
                              {integration.displayName || integration.provider}
                            </span>
                            <span className={styles.checkboxMeta}>
                              {integration.accountInfo?.name || 'Connected'}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          {/* Footer */}
          <div className={styles.footer}>
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              leftIcon={isSaving ? <Loader2 size={16} className={styles.spinning} /> : <Save size={16} />}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAssistantModal;
